import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable no-param-reassign */
import _map from 'lodash/map';
import _isFunction from 'lodash/isFunction';
import _isArray from 'lodash/isArray';
import _isObject from 'lodash/isObject';
import _forEach from 'lodash/forEach';
import _isNil from 'lodash/isNil';
import _pick from 'lodash/pick';
import _get from 'lodash/get';
import _startsWith from 'lodash/startsWith';
import { ID_FIELD_NAME, USER_FIRST_NAME_FIELD_NAME, USER_LAST_NAME_FIELD_NAME, DEALER_ID_FIELD_NAME, TEK_SITE_ID_FIELD_NAME } from '../../readers/User';
import TEnvReader from '../../readers/Env';
import { EMPTY_OBJECT, EMPTY_ARRAY } from '../../app.constants';
import { JAVASCRIPT_ERROR, NETWORK_ERROR, NETWORK_ERROR_STATUS_CODES } from './sentryErrorTracker.constants';

function isNetworkError(errorValue) {
  /**
   * Limiting to only most common network status error codes.
   */
  return NETWORK_ERROR_STATUS_CODES.some(function (errorCode) {
    return _startsWith(errorValue, "".concat(errorCode, " at "));
  });
}

function getErrorType(errorValue) {
  if (isNetworkError(errorValue)) return NETWORK_ERROR;
  return JAVASCRIPT_ERROR;
}

function beforeErrorSend(event, hint) {
  var exceptionValue = _get(event, ['exception', 'values', 0, 'value'], '');

  var _window = window,
      FS = _window.FS;
  if (!FS) return event;
  var error = hint.originalException || {};
  var currentSessionURL = _isFunction(FS.getCurrentSessionURL) && FS.getCurrentSessionURL(true) || 'current session URL API not ready';
  event.extra = event.extra || {};
  event.tags = event.tags || {};
  event.extra.fullstory = currentSessionURL;
  event.tags.fullstory = currentSessionURL;
  event.tags.error_type = getErrorType(exceptionValue);
  FS.event('Application Error', {
    name_str: error.name,
    message_str: error.message,
    fileName_str: error.fileName,
    lineNumber_str: error.lineNumber,
    stack_str: error.stack,
    sentryEventId_str: hint.event_id
  });
  return event;
}

var beforeBreadcrumbSend = function beforeBreadcrumbSend(breadcrumb, hint) {
  if (breadcrumb.category !== 'xhr') return breadcrumb;

  var _ref = hint || EMPTY_OBJECT,
      xhr = _ref.xhr;

  if (!(xhr instanceof XMLHttpRequest)) return breadcrumb; // safe-check

  if (xhr.status === 200) return breadcrumb;
  breadcrumb.data = _objectSpread(_objectSpread({}, breadcrumb.data), {}, {
    response: xhr.response,
    responseHeaders: xhr.getAllResponseHeaders()
  });
  return breadcrumb;
};

function initErrorTracker(orgSlug, projectSlug) {
  if (process.env.NODE_ENV === 'development') return; // eslint-disable-next-line global-require

  var Sentry = require('@sentry/browser');

  var dsnEnvVarPresent = TEnvReader.sentryDsn(process.env);
  var isSentryConfigPresent = !(_isNil(dsnEnvVarPresent) && (_isNil(orgSlug) || _isNil(projectSlug)));
  if (!Sentry || !isSentryConfigPresent) return;
  window.Sentry = Sentry;
  var sentryConfig = {
    release: TEnvReader.buildVersionWithEnv(process.env),
    environment: TEnvReader.buildEnv(process.env),
    beforeSend: beforeErrorSend,
    beforeBreadcrumb: beforeBreadcrumbSend,
    dsn: _isNil(dsnEnvVarPresent) ? "https://".concat(orgSlug, "@sentry.io/").concat(projectSlug) : dsnEnvVarPresent
  };
  Sentry.init(sentryConfig);
}

function getSentryInstance() {
  return window.Sentry;
}

function makeUserConfig(user) {
  // Return only selected user details (id (userId), lastName, firstName, dealerId, tekSiteId)
  return _pick(user, [ID_FIELD_NAME, USER_FIRST_NAME_FIELD_NAME, USER_LAST_NAME_FIELD_NAME, DEALER_ID_FIELD_NAME, TEK_SITE_ID_FIELD_NAME]);
}

function makeEnvConfig(user, env) {
  return _objectSpread(_objectSpread({}, env), {}, {
    tenantName: user.tenantName
  });
}

function setUserContext(user) {
  var Sentry = getSentryInstance();
  if (!Sentry) return;
  var userConfig = makeUserConfig(user);
  Sentry.configureScope(function (scope) {
    scope.setUser(userConfig);
  });
}

function setEnvContext(user, env) {
  var Sentry = getSentryInstance();
  if (!Sentry) return;
  var envConfig = makeEnvConfig(user, env);
  Sentry.configureScope(function (scope) {
    _map(envConfig, function (value, key) {
      return scope.setTag(key, value);
    });
  });
}

function sendException(error, addl) {
  var Sentry = getSentryInstance();
  if (!Sentry) return;

  var _ref2 = addl || EMPTY_OBJECT,
      context = _ref2.context,
      groupingParams = _ref2.groupingParams;

  var sanitizedGroupingParams = _isArray(groupingParams) ? groupingParams : EMPTY_ARRAY;
  var sanitizedContext = _isObject(context) ? context : EMPTY_OBJECT;
  Sentry.withScope(function (scope) {
    scope.setFingerprint(['{{ default }}'].concat(_toConsumableArray(sanitizedGroupingParams)));

    _forEach(sanitizedContext, function (value, key) {
      scope.setExtra(key, JSON.stringify(value));
    });

    _forEach(sanitizedGroupingParams, function (value, index) {
      scope.setExtra("Grouping param-".concat(index + 1), value);
    });

    Sentry.captureException(error);
  });
}

function logMessage(message, addl) {
  var Sentry = getSentryInstance();
  if (!Sentry) return;

  var _ref3 = addl || EMPTY_OBJECT,
      context = _ref3.context,
      groupingParams = _ref3.groupingParams;

  var sanitizedGroupingParams = _isArray(groupingParams) ? groupingParams : EMPTY_ARRAY;
  var sanitizedContext = _isObject(context) ? context : EMPTY_OBJECT;
  Sentry.withScope(function (scope) {
    scope.setFingerprint(['{{ default }}'].concat(_toConsumableArray(sanitizedGroupingParams)));

    _forEach(sanitizedContext, function (value, key) {
      scope.setExtra(key, JSON.stringify(value));
    });

    _forEach(sanitizedGroupingParams, function (value, index) {
      scope.setExtra("Grouping param-".concat(index + 1), value);
    });

    Sentry.captureMessage(message);
  });
}

export { initErrorTracker, setUserContext, setEnvContext, sendException, logMessage, getErrorType as __testGetErrorType__ };